import React, { useState, useCallback, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import BottomNavigation from "./BottomNavigation";
import HamburgerMenu from "./HamburgerMenu";
import { Menu, Send, Mic, MicOff, X, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { useListContext } from "../contexts/ListContext";
import { useAuth } from "../contexts/AuthContext";
import { UpgradeBanner, AdPlaceholder } from "./SubscriptionGate";

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [aiResponse, setAiResponse] = useState("");
  const [showAiResponse, setShowAiResponse] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const { token } = useAuth();
  
  // Use ref to store recognition instance to avoid stale closures
  const recognitionRef = useRef(null);

  // Fetch unread notification count
  useEffect(() => {
    if (!token) return;
    const fetchUnread = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/notifications?limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const d = await res.json();
          setUnreadCount(d.unread || 0);
        }
      } catch (e) { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [token, location.pathname]);
  
  // Get list context
  const { 
    lists, 
    currentList, 
    currentListId, 
    addItemsToList, 
    findListByName 
  } = useListContext();

  // Parse quantity from input text (same logic as Home component)
  const parseQuantityFromInput = (input) => {
    const trimmedInput = input.trim();
    const match = trimmedInput.match(/^(\d+)\s+(.+)$/);
    
    if (match) {
      const quantity = parseInt(match[1], 10);
      const itemName = match[2];
      return { quantity, itemName };
    }
    
    return { quantity: 1, itemName: trimmedInput };
  };

  // Convert number words to digits for voice input processing
  const convertNumberWordsToDigits = (text) => {
    const numberWords = {
      'zero': '0', 'one': '1', 'two': '2', 'three': '3', 'four': '4', 'five': '5',
      'six': '6', 'seven': '7', 'eight': '8', 'nine': '9', 'ten': '10',
      'eleven': '11', 'twelve': '12', 'thirteen': '13', 'fourteen': '14', 'fifteen': '15',
      'sixteen': '16', 'seventeen': '17', 'eighteen': '18', 'nineteen': '19', 'twenty': '20',
      'thirty': '30', 'forty': '40', 'fifty': '50', 'sixty': '60', 'seventy': '70',
      'eighty': '80', 'ninety': '90', 'hundred': '100'
    };

    let convertedText = text;
    
    // Handle simple number word replacements
    Object.entries(numberWords).forEach(([word, digit]) => {
      // Use word boundaries to avoid partial matches
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      convertedText = convertedText.replace(regex, digit);
    });

    // Handle compound numbers like "twenty one", "thirty five" etc.
    convertedText = convertedText.replace(/\b(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)\s+(one|two|three|four|five|six|seven|eight|nine)\b/gi, 
      (match, tens, ones) => {
        const tensValue = numberWords[tens.toLowerCase()] || tens;
        const onesValue = numberWords[ones.toLowerCase()] || ones;
        return (parseInt(tensValue) + parseInt(onesValue)).toString();
      });

    console.log('Converted voice input:', text, '→', convertedText);
    return convertedText;
  };

  const getPageTitle = () => {
    switch (location.pathname) {
      case "/": return "Foodz Lizts";
      case "/home": return "Grocery List";
      case "/meal-plans": return "Meal Plans";
      case "/scan": return "Scan · Coming Soon";
      case "/recipes": return "Recipes";
      case "/deals": return "Deals";
      case "/budget": return "Budget";
      default: return "Foodz Lizts";
    }
  };

  const processAIInput = useCallback(async (inputData, inputMethod = "text") => {
    if (!inputData.trim()) return;
    
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      // Convert number words to digits for voice input
      let processedInput = inputData;
      if (inputMethod === "voice") {
        processedInput = convertNumberWordsToDigits(inputData);
        console.log('Voice input converted from:', inputData, 'to:', processedInput);
      }
      
      console.log('Processing AI input:', processedInput, 'Method:', inputMethod);
      
      // Prepare list context with all user lists for AI
      const listContext = {
        name: currentList?.name || "My Grocery List",
        available_lists: lists.map(list => ({
          id: list.id,
          name: list.name,
          color: list.color,
          items: list.items || [],
          budget: list.budget
        })),
        items: currentList?.items || [],
        budget: currentList?.budget || 0,
        color: currentList?.color || "green"
      };
      
      const response = await fetch(`${BACKEND_URL}/api/ai/process-input`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: "default_user",
          list_id: currentListId?.toString() || "default_list",
          input_data: processedInput, // Use converted input
          input_method: inputMethod,
          list_context: listContext,
          user_preferences: {
            dietary_restrictions: [],
            household_size: 1,
            favorite_brands: []
          }
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setAiResponse(result.ai_response);
        setShowAiResponse(true);
        
        // Handle multi-list AI responses - now parsing converted input for quantities
        if (result.items_by_list && Object.keys(result.items_by_list).length > 0) {
          // Items distributed to specific lists
          for (const [listName, items] of Object.entries(result.items_by_list)) {
            const targetList = findListByName(listName);
            if (targetList && items.length > 0) {
              const formattedItems = items.map((item, index) => {
                console.log('AI item before parsing:', item.name);
                // Try to extract quantity from the CONVERTED user input (voice numbers now as digits)
                let quantity = 1;
                let itemName = item.name;
                
                // Look for this item in the converted input to get quantity
                const inputLower = processedInput.toLowerCase();
                const itemLower = item.name.toLowerCase();
                
                // Simple regex to find "number + item" pattern in converted input
                const quantityRegex = new RegExp(`(\\d+)\\s+[\\w\\s]*${itemLower}`, 'i');
                const match = inputLower.match(quantityRegex);
                if (match) {
                  quantity = parseInt(match[1], 10) || 1;
                  console.log(`Found quantity ${quantity} for ${itemName} in converted input`);
                }
                
                return {
                  id: Date.now() + Math.random() + index,
                  name: itemName,
                  quantity: quantity,
                  completed: false,
                  category: item.category
                };
              });
              addItemsToList(targetList.id, formattedItems);
            }
          }
        } else if (result.items_to_add && result.items_to_add.length > 0) {
          // Fallback: add to current list - parse quantities from converted input
          const itemsWithQuantity = result.items_to_add.map((item, index) => {
            console.log('AI fallback item before parsing:', item.name);
            
            // Try to extract quantity from the CONVERTED user input
            let quantity = 1;
            let itemName = item.name;
            
            // Look for this item in the converted input to get quantity
            const inputLower = processedInput.toLowerCase();
            const itemLower = item.name.toLowerCase();
            
            // Simple regex to find "number + item" pattern in converted input
            const quantityRegex = new RegExp(`(\\d+)\\s+[\\w\\s]*${itemLower}`, 'i');
            const match = inputLower.match(quantityRegex);
            if (match) {
              quantity = parseInt(match[1], 10) || 1;
              console.log(`Found quantity ${quantity} for ${itemName} in converted input`);
            }
            
            return {
              id: Date.now() + Math.random() + index,
              name: itemName,
              quantity: quantity,
              completed: false,
              category: item.category || 'Other'
            };
          });
          
          window.dispatchEvent(new CustomEvent('addAIItems', { 
            detail: itemsWithQuantity 
          }));
        }
        
        // Auto-hide response after 8 seconds
        setTimeout(() => {
          setShowAiResponse(false);
        }, 8000);
      } else {
        setAiResponse(result.ai_response || "I'm having trouble processing your request. Please try again.");
        setShowAiResponse(true);
      }
      
    } catch (error) {
      console.error('AI processing error:', error);
      setAiResponse("I'm having trouble connecting to my brain right now. Please try again in a moment!");
      setShowAiResponse(true);
    }
  }, [lists, currentList, currentListId, findListByName, addItemsToList]);

  const handleClearInput = () => {
    setAiInput("");
  };

  const handleAIInput = () => {
    if (aiInput.trim()) {
      processAIInput(aiInput, "text");
      setAiInput("");
    }
  };

  const handleVoiceInput = useCallback(() => {
    console.log("handleVoiceInput called, isListening:", isListening);
    
    if (!isListening) {
      // Check for speech recognition support
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        setAiResponse("Voice recognition is not supported in your browser. Please use Chrome, Edge, or Safari.");
        setShowAiResponse(true);
        return;
      }

      // Use the appropriate speech recognition API
      const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
      const recognition = new SpeechRecognition();
      
      // Configure recognition settings
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;
      
      recognition.onstart = () => {
        console.log('✅ Voice recognition started - updating UI');
        setIsListening(true);
        setAiInput("");
        setAiResponse("🎤 Listening... Speak now!");
        setShowAiResponse(true);
      };
      
      recognition.onresult = (event) => {
        console.log('✅ Voice recognition result received');
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        // Show interim results
        if (interimTranscript && !finalTranscript) {
          setAiInput(interimTranscript);
          setAiResponse("🎤 Listening... " + interimTranscript);
          setShowAiResponse(true);
        }
        
        // Process final transcript
        if (finalTranscript) {
          console.log('✅ Final voice input received:', finalTranscript);
          setAiInput(finalTranscript);
          // Clean status while waiting for backend — don't echo raw transcript
          setAiResponse("Adding items to your list...");
          setShowAiResponse(true);
          
          // Process the voice input
          setTimeout(() => {
            processAIInput(finalTranscript, "voice");
          }, 500);
        }
      };
      
      recognition.onerror = (event) => {
        console.error('❌ Voice recognition error:', event.error);
        setIsListening(false);
        
        let errorMessage = "Voice recognition failed. ";
        switch (event.error) {
          case 'no-speech':
            errorMessage += "No speech was detected. Please try again.";
            break;
          case 'audio-capture':
            errorMessage += "Microphone access denied or not available.";
            break;
          case 'not-allowed':
            errorMessage += "Microphone permission denied. Please enable microphone access in your browser settings.";
            break;
          case 'network':
            errorMessage += "Network error occurred. Please check your connection.";
            break;
          case 'aborted':
            errorMessage = "Voice input cancelled.";
            break;
          default:
            errorMessage += `Error: ${event.error}. Please try again or use text input.`;
        }
        
        setAiResponse(errorMessage);
        setShowAiResponse(true);
        setAiInput("");
      };
      
      recognition.onend = () => {
        console.log('Voice recognition ended - resetting UI');
        setIsListening(false);
      };
      
      // Start recognition
      try {
        console.log('🎤 Starting voice recognition...');
        recognition.start();
        recognitionRef.current = recognition;
      } catch (error) {
        console.error('❌ Failed to start voice recognition:', error);
        setAiResponse("Failed to start voice recognition. Please ensure microphone permissions are enabled in your browser settings.");
        setShowAiResponse(true);
        setIsListening(false);
      }
    } else {
      // Stop listening if currently active
      console.log('🛑 Stopping voice recognition...');
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.log('Recognition already stopped');
        }
        recognitionRef.current = null;
      }
      setIsListening(false);
      setShowAiResponse(false);
      setAiInput("");
    }
  }, [isListening, processAIInput]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-4">
      {/* Sticky Floating Header with AI Assistant */}
      <div className="px-4 mb-4 sticky top-4 z-20">
        <header className="bg-black text-white px-4 py-3 rounded-lg shadow-lg">
          <div className="flex justify-between items-center">
            <div className="flex-shrink-0">
              <h1 className="text-xl font-bold text-green-400">Foodz Lizts</h1>
              <p className="text-sm text-gray-300">{getPageTitle()}</p>
            </div>
            
            {/* AI Assistant Input - shown on Home dashboard */}
            {location.pathname === "/home" && (
              <div className="flex-1 max-w-md mx-4">
                <div className="space-y-2">
                  {/* Top row: Full-width input field */}
                  <div className="w-full">
                    <Input
                      placeholder="Ask AI... (type or speak your grocery needs)"
                      value={aiInput}
                      onChange={(e) => setAiInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleAIInput()}
                      className="w-full bg-gray-800 border-gray-700 text-white placeholder-gray-400 text-sm h-10 px-3"
                    />
                  </div>
                  
                  {/* Bottom row: Control buttons */}
                  <div className="flex items-center justify-between gap-2">
                    {/* Left: Clear button */}
                    <Button
                      onClick={handleClearInput}
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-white hover:bg-gray-700 h-8 w-8 p-0"
                      title="Clear input"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    
                    {/* Center: Voice button */}
                    <Button
                      onClick={handleVoiceInput}
                      variant="ghost"
                      size="sm"
                      className={`h-8 w-8 p-0 ${
                        isListening 
                          ? "text-red-400 hover:bg-gray-700" 
                          : "text-gray-400 hover:bg-gray-700"
                      }`}
                      title={isListening ? "Stop listening" : "Voice input"}
                    >
                      {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                    </Button>
                    
                    {/* Right: Send button */}
                    <Button 
                      onClick={handleAIInput}
                      variant="ghost"
                      size="sm"
                      className="text-green-400 hover:bg-gray-700 h-8 w-8 p-0"
                      title="Send message"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(true)}
              className="text-white hover:bg-gray-800 flex-shrink-0 relative"
              data-testid="hamburger-menu-btn"
            >
              <Menu className="h-6 w-6" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="notif-badge">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>
          </div>
        </header>
      </div>

      {/* AI Response Notification */}
      {showAiResponse && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mx-4 mb-4 rounded-r-lg shadow-sm">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Sparkles className="h-3 w-3 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-green-800">AI Assistant</p>
                <p className="text-sm text-green-700 mt-1">{aiResponse}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAiResponse(false)}
              className="text-green-600 hover:text-green-800 h-6 w-6 p-0"
            >
              ×
            </Button>
          </div>
        </div>
      )}

      {/* Upgrade Banner */}
      <UpgradeBanner />

      {/* Ad Placeholder (free users only) */}
      <AdPlaceholder position="banner" />

      {/* Main Content */}
      <main className="flex-1 pb-20">
        {children}
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation />

      {/* Hamburger Menu */}
      <HamburgerMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </div>
  );
};

export default Layout;